import {
  Box,
  Button,
  Chip,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import {
  assetImportRowSchema,
  partialImportRowSchema,
} from '../../../schemas/asset';
import {
  getSafeTextFromText,
  handlePlural,
  parseXLSX,
  readURLAsBinary,
} from '../../../shared/utilities';

import { makeStyles } from '@mui/styles';
import { BigBatch } from '@qualdesk/firestore-big-batch';
import firebase from 'firebase/app';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import { getFirebase } from 'react-redux-firebase';
import { z } from 'zod';
import FileUploaderCard from '../../../components/FileUploaderCard';
import { useModal } from '../../../components/Modal';
import { colours } from '../../../config/theme';
import { getAssetListById } from '../../../hooks/useAssetList';
import { getLastUpdated } from '../../../shared/logTools';
import {
  FORM_CONFIG_DOC_ID,
  resolveSurveyFormConfig,
} from '../../../state/form-config';
import { dateSchema } from '../../../shared/validation';
import { AssetAttributes } from '../../../types/asset';
import { FormField } from '../../../types/form';
import { SurveyProps } from '../../../types/survey';
import { UrlParams } from '../../../types/urlParams';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    height: '100%',
  },
}));

const IGNORE_COLUMNS = [
  'Created',
  'Last Updated',
  'Site Name',
  'Survey Name',
  'Building Name',
  'Room Name',
  'Surveyor Name',
  'Floor Name',
  'Name',
  'Renewal Cost',
  'ID',
  'Relationship',
  'Parent ID',
  'Image 1',
  'Image 2',
  'Image 3',
  'Image 4',
  'Image 5',
];

const PARTIAL_IMPORT_MAP = {
  ['Reccurs']: 'reccurs',
  ['Expected Life']: 'reccursPeriod',
  ['Remaining Life']: 'nextRenewal',
  ['UOM']: 'uom',
  ['Renewal Multiplier']: 'costPercentage',
  ['Description']: 'description',
};

const ASSET_ATTRIBUTES_MAP = {
  ['Label 0']: 'label0',
  ['Code 0']: 'code0',
  ['Label 1']: 'label1',
  ['Code 1']: 'code1',
  ['Label 2']: 'label2',
  ['Code 2']: 'code2',
  ['Label 3']: 'label3',
  ['Code 3']: 'code3',
  ['Cost']: 'costPerUnit',
  ['UOM']: 'uom',
  ['Uplift Multiplier']: 'uplift',
  ['Expected Life']: 'expectedLife',
};

const NOT_APPLICABLE = 'N/A';
const CHILD_ROW = 'Child';
// ...existing code...

type InitialData = {
  surveyId?: string;
  formFields?: FormField[];
  assetList?: AssetAttributes[];
  errors: ImportError[];
};

type ImportRow = {
  Created: string;
  'Last Updated': string;
  Relationship: string;
  'Site Name': string;
  'Building Name': string;
  'Survey Name': string;
  'Room Name': string;
  'Floor Name': string;
  'Surveyor Name': string;
  'Code 0': string;
  'Label 0': string;
  'Code 1': string;
  'Label 1': string;
  'Code 2': string;
  'Label 2': string;
  'Code 3': string;
  'Label 3': string;
  Name: string;
  Description: string;
  Quantity: number;
  UOM: string;
  Cost: number;
  'Uplift Multiplier': number;
  'Renewal Multiplier': number;
  'Renewal Cost': number;
  'Remaining Life': number;
  'Expected Life': number;
  Reccurs: string;
  ID: string;
  'Parent ID': string;
  'Image 1': string;
  'Image 2': string;
  'Image 3': string;
  'Image 4': string;
  'Image 5': string;
};

type ImportError = {
  row: string | number;
  column: string | number;
  message: string;
};

type UpdateResult = {
  success?: { type: 'asset' | 'partial'; assetId: string; partialId?: string };
  error?: {
    row: number;
    column: string;
    message: string;
  };
  warning?: {
    row: number;
    column: string;
    message: string;
  };
};

type ImportData = {
  name: string;
  mappedRows: Map<string, (ImportRow | Partial<ImportRow>)[]>;
  allRows: Partial<ImportRow>[];
  counters: {
    assets: number;
    partials: number;
  };
  errors: ImportError[];
  formFields?: FormField[];
  assetList?: AssetAttributes[];
  surveyId?: string;
  // Add these for downstream use
  assetIdToSurveyId: Record<string, string>;
  surveyIdToFormFields: Record<string, FormField[]>;
  surveyIdToAssetList: Record<string, AssetAttributes[]>;
} & InitialData;

const errorMap: z.ZodErrorMap = (error, ctx) => {
  /*
  This is where you override the various error codes
  */
  const [row, column] = error.path;

  // Add logging for Cost column failures to debug validation issues
  if (column === 'Cost' && error.code === z.ZodIssueCode.invalid_type) {
    console.error('❌ Cost validation failed:', {
      row: row,
      column: column,
      receivedValue: ctx.data,
      receivedType: typeof ctx.data,
      expectedType: error.expected,
      rawValue: JSON.stringify(ctx.data),
      valueLength: typeof ctx.data === 'string' ? ctx.data.length : 'N/A',
    });
  }

  switch (error.code) {
    case z.ZodIssueCode.invalid_type:
      return {
        message: `Expected ${error.expected} but received ${
          error.received
        } (value: ${JSON.stringify(ctx.data)})`,
      };
    case z.ZodIssueCode.invalid_union: {
      const firstIssue = error.unionErrors[0].issues[0];

      if (
        firstIssue.code === z.ZodIssueCode.invalid_type &&
        column !== 'Code 3'
      ) {
        return {
          message: `Expected a ${firstIssue.expected}`,
        };
      }
      break;
    }
    case z.ZodIssueCode.invalid_enum_value:
      if (column === 'Code 3') {
        return {
          message: `${ctx.data} does not match any Level 3 asset codes in your taxonomy`,
        };
      }
      if (error.options.length <= 10) {
        return {
          message: `"${
            ctx.data
          }" does not match any of the valid options - "${error.options.join(
            ', ',
          )}"`,
        };
      }
      break;
  }

  // fall back to default message!
  return { message: ctx.defaultError };
};

async function checkForMultipleSurveys(data) {
  // const surveyNames = new Set(data.rows.map((r) => r['Survey Name']));
  // if (surveyNames.size > 1) {
  //   data.errors.push(
  //     `Multiple surveys - You can only import assets for a single survey, you currently have assets for ${Array.from(
  //       surveyNames,
  //     ).join(', ')}`,
  //   );
  // }
}

// TODO - need to stop deleted assets from being included for the survey fetching
function removeDeletedAssets(docs) {
  return docs.filter((doc) => doc.data().deleted === 0);
}

async function getInitialDataForAssetIds(assetIds: string[], clientId: string) {
  const fireStore = firebase.firestore();
  const assetIdToSurveyId: Record<string, string> = {};
  const assetIdToAssetData: Record<string, any> = {};

  // Batch queries using Firestore 'in' operator (max 10 items per query)
  const BATCH_SIZE = 10;
  const batches: string[][] = [];

  for (let i = 0; i < assetIds.length; i += BATCH_SIZE) {
    batches.push(assetIds.slice(i, i + BATCH_SIZE));
  }

  console.log(
    `🔍 Fetching ${assetIds.length} asset(s) in ${batches.length} batch(es)...`,
  );

  // Query in batches to reduce Firestore reads
  for (const batch of batches) {
    const assetDocs = await fireStore
      .collectionGroup('assets')
      .where('clientId', '==', clientId)
      .where('id', 'in', batch)
      .get();

    removeDeletedAssets(assetDocs.docs).forEach((doc) => {
      const assetData = doc.data();
      assetIdToSurveyId[assetData.id] = assetData.surveyId;
      assetIdToAssetData[assetData.id] = assetData;
    });
  }

  const surveyIdToSurveyData: Record<string, SurveyProps> = {};
  const surveyIdToAssetList: Record<string, AssetAttributes[]> = {};
  const surveyIdToFormFields: Record<string, FormField[]> = {};
  const uniqueSurveyIds = Array.from(new Set(Object.values(assetIdToSurveyId)));

  for (const surveyId of uniqueSurveyIds as string[]) {
    if (!surveyId) continue;
    const surveyDoc = await fireStore
      .collection('clients')
      .doc(clientId)
      .collection('surveys')
      .doc(surveyId)
      .get();
    if (surveyDoc.exists) {
      const surveyData = surveyDoc.data() as SurveyProps;
      const formConfigSnap = await fireStore
        .collection('clients')
        .doc(clientId)
        .collection('surveys')
        .doc(surveyId)
        .collection('config')
        .doc(FORM_CONFIG_DOC_ID)
        .get();
      const { formFields, formOptions } = resolveSurveyFormConfig({
        surveyDocData: surveyData,
        formConfigData: formConfigSnap.exists ? formConfigSnap.data() : {},
        context: 'asset-data-import',
        surveyId,
      });
      if (!formFields?.length) {
        throw new Error(`Survey ${surveyDoc.id} is missing formFields!`);
      }
      surveyIdToSurveyData[surveyId] = {
        ...surveyData,
        formFields,
        formOptions,
      };
      surveyIdToFormFields[surveyId] = formFields;
      surveyIdToAssetList[surveyId] = await getAssetListById(
        clientId,
        surveyData.assetListId,
      );
    }
  }
  return {
    assetIdToSurveyId,
    assetIdToAssetData,
    surveyIdToSurveyData,
    surveyIdToAssetList,
    surveyIdToFormFields,
  };
}

function parseAssetUpdate(
  existingAsset,
  assetUpdate,
  allowedValues,
  formFields,
  assetList = [] as AssetAttributes[],
) {
  const parsed = cloneDeep(existingAsset);

  for (const [key, value] of Object.entries(assetUpdate)) {
    // This is for a very edge case where one of the form field options is
    // N/A, in which case we don't want to ignore updating the value

    // Only try to find formField if formFields is defined
    const formField = formFields?.find((ff) => {
      return ff.label === key;
    });
    const hasNotApplicableOption = formField?.options?.find(
      (o) => o.value === NOT_APPLICABLE,
    );
    if (
      (value === NOT_APPLICABLE && !hasNotApplicableOption) ||
      IGNORE_COLUMNS.includes(key) ||
      // formfield might not exist if it's not a form field ie Cost, UOM
      (typeof value === 'undefined' && formField?.required)
    ) {
      continue;
    }

    const transformedAttributeProp = ASSET_ATTRIBUTES_MAP[key];
    if (transformedAttributeProp) {
      parsed.attributes[transformedAttributeProp] = value;
    } else if (
      Object.keys(allowedValues).includes(key) &&
      typeof value !== 'undefined'
    ) {
      parsed.values[getSafeTextFromText(key)] = value;
    }
  }

  if (assetUpdate['Code 3'] !== existingAsset.code) {
    const matchingAssetAttributes = assetList.find(
      (a) => a.code3 === assetUpdate['Code 3'],
    );
    const previousAssetAttributes = assetList.find(
      (a) => a.code3 === existingAsset.code,
    );
    if (matchingAssetAttributes) {
      parsed.attributes.code0 = matchingAssetAttributes.code0;
      parsed.attributes.label0 = matchingAssetAttributes.label0;
      parsed.attributes.code1 = matchingAssetAttributes.code1;
      parsed.attributes.label1 = matchingAssetAttributes.label1;
      parsed.attributes.code2 = matchingAssetAttributes.code2;
      parsed.attributes.label2 = matchingAssetAttributes.label2;
      parsed.attributes.code3 = matchingAssetAttributes.code3;
      parsed.attributes.label3 = matchingAssetAttributes.label3;
      // if the previous value matches existing, update to new value
      // otherwise they will have been updated manually so they should be kept
      if (
        previousAssetAttributes?.costPerUnit ===
        existingAsset.attributes.costPerUnit
      ) {
        parsed.attributes.costPerUnit = matchingAssetAttributes.costPerUnit;
      }
      if (previousAssetAttributes?.uom === existingAsset.attributes.uom) {
        parsed.attributes.uom = matchingAssetAttributes.uom;
      }
      if (
        previousAssetAttributes?.expectedLife ===
        existingAsset.attributes.expectedLife
      ) {
        parsed.attributes.expectedLife = matchingAssetAttributes.expectedLife;
      }
      if (previousAssetAttributes?.uplift === existingAsset.attributes.uplift) {
        parsed.attributes.uplift = matchingAssetAttributes.uplift;
      }
    }
  }

  return parsed;
}

// Helper to convert Reccurs string to boolean for DB
function reccursStringToBool(val: string | undefined): boolean | undefined {
  if (val === 'YES') return true;
  if (val === 'NO') return false;
  return undefined; // for 'N/A' or undefined
}

function parsePartialUpdate(existingPartial, partialUpdate) {
  const parsed = { ...existingPartial };
  for (const [key, value] of Object.entries(partialUpdate)) {
    if (value === NOT_APPLICABLE) {
      continue;
    }
    const transformedProp = PARTIAL_IMPORT_MAP[key];
    if (transformedProp) {
      // Special handling for Reccurs
      if (key === 'Reccurs') {
        parsed[transformedProp] = reccursStringToBool(value as string);
      } else {
        parsed[transformedProp] = value;
      }
    }
  }
  return parsed;
}

// Refactored: updateAssetFromImportRow now uses BigBatch for Firestore updates
async function updateAssetFromImportRow(
  assetId: string,
  clientId: string,
  data: ImportData,
  formFields: FormField[],
  assetList: AssetAttributes[] = [] as AssetAttributes[],
): Promise<UpdateResult[]> {
  const updates = data.mappedRows.get(assetId);

  const fireStore = getFirebase().firestore();
  const queryResult = await fireStore
    .collectionGroup('assets')
    .where('clientId', '==', clientId)
    .where('id', '==', assetId)
    .get();
  const updateResults: UpdateResult[] = [];
  if (!queryResult.size) {
    //need to add 2 to cater for 0 index of array and also header row
    const rowNum = data.allRows.findIndex((r) => r['ID'] === assetId) + 2;
    updateResults.push({
      error: {
        row: rowNum,
        column: 'ID',
        message: `No asset with id ${assetId} found`,
      },
    });
    return updateResults;
  }
  const assetDoc = removeDeletedAssets(queryResult?.docs)?.[0];
  const assetData = assetDoc?.data();
  if (assetData && updates) {
    let assetUpdateObj = cloneDeep(assetData);
    // Use BigBatch for all updates to this asset
    const bigBatch = new BigBatch({ firestore: fireStore });
    for (const update of updates) {
      const isPartial = update['Relationship'] === CHILD_ROW;
      const id = update['ID'];
      //need to add 2 to cater for 0 index of array and also header row
      const rowNum = data.allRows.findIndex((r) => r['ID'] === id) + 2;
      if (isPartial) {
        if (!id) {
          updateResults.push({
            error: {
              row: rowNum,
              column: 'ID',
              message: `Partial renewal row is missing an ID`,
            },
          });
          continue;
        }
        const existingPartial = assetData.partials[id];
        if (!existingPartial) {
          updateResults.push({
            error: {
              row: rowNum,
              column: 'ID',
              message: `No partial renewal with id ${id} found`,
            },
          });
          continue;
        }
        assetUpdateObj.partials[update['ID']] = parsePartialUpdate(
          existingPartial,
          update,
        );
        if (isEqual(assetUpdateObj.partials[update['ID']], existingPartial)) {
          updateResults.push({
            warning: {
              row: rowNum,
              column: 'ID',
              message: `Partial renewal with id ${id} was not updated as there were no changes to update`,
            },
          });
        } else {
          bigBatch.update(assetDoc.ref, {
            [`partials.${id}`]: assetUpdateObj.partials[update['ID']],
            ...getLastUpdated(),
          });
          updateResults.push({
            success: { type: 'partial', assetId, partialId: id },
          });
        }
      } else {
        // Use correct formFields and assetList for this asset's survey
        const allowedValues = (formFields || []).reduce((acc, ff) => {
          return { ...acc, [ff.label]: ff.id };
        }, {});
        assetUpdateObj = parseAssetUpdate(
          assetUpdateObj,
          update,
          allowedValues,
          formFields,
          assetList,
        );
        if (!isEqual(assetData, assetUpdateObj)) {
          assetUpdateObj.label = assetUpdateObj.attributes.label3;
          assetUpdateObj.code = assetUpdateObj.attributes.code3;
          bigBatch.update(assetDoc.ref, {
            ...assetUpdateObj,
            ...getLastUpdated(),
          });
          updateResults.push({
            success: { type: 'asset', assetId },
          });
        } else {
          updateResults.push({
            warning: {
              row: rowNum,
              column: 'ID',
              message: `Asset with id ${assetId} was not updated as there were no changes to update`,
            },
          });
        }
      }
    }
    await bigBatch.commit();
  }
  return updateResults;
}

function checkForValidAssetType(row, assetList) {
  if (assetList) {
    return assetList.find((item) => item.code3 === row['Code 3']);
  }
}

function sortByRow(a, b) {
  return a.row - b.row;
}

const AssetDataImport = () => {
  const fileUploaderRef = useRef();
  const { clientId } = useParams<UrlParams>();
  const [initialErrors, setInitialErrors] = useState<ImportError[]>();
  const [importData, setImportData] = useState<ImportData>();
  const [updateResults, setUpdateResults] = useState<UpdateResult[]>([]);
  const [updateTotal, setUpdateTotal] = useState(0);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [isValidating, setIsValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const { showModal, closeModal } = useModal();

  const setFileData = async (file: File) => {
    if (!clientId) {
      return console.error('Client ID is required to import asset data');
    }
    // show overlay modal immediately so the user sees progress
    // @ts-ignore
    showModal({
      title: 'Validating import',
      messages: ['Please wait while we validate your file...'],
      buttons: {},
    });
    // yield to the browser so modal can render
    await new Promise((r) => setTimeout(r, 0));

    const { name } = file;
    const data = await readURLAsBinary(file);
    setUpdateResults([]);
    const assetImports = await parseXLSX<ImportRow>(data, undefined);

    console.log(
      `📊 Processing import file with ${assetImports.rows.length} total rows...`,
    );

    // ...existing code...

    // Group rows by composite key: Survey Name + Site Name + Building Name
    const groupKey = (row: ImportRow) =>
      `${row['Survey Name']}|${row['Site Name']}|${row['Building Name']}`;
    const compositeToRows: Record<string, ImportRow[]> = {};
    let skippedRows = 0;
    assetImports.rows.forEach((row, idx) => {
      const surveyName = row['Survey Name'];
      const siteName = row['Site Name'];
      const buildingName = row['Building Name'];
      if (!surveyName || !siteName || !buildingName) {
        console.warn(
          `Skipping row ${idx + 2} due to missing composite key fields:`,
          { surveyName, siteName, buildingName, row },
        );
        skippedRows++;
        return;
      }
      const key = `${surveyName}|${siteName}|${buildingName}`;
      if (!compositeToRows[key]) compositeToRows[key] = [];
      // attach original row index so validation errors can be mapped back
      compositeToRows[key].push({ ...(row as any), __rowIndex: idx });
    });

    console.log(
      `📦 Grouped into ${
        Object.keys(compositeToRows).length
      } composite group(s) (Survey + Site + Building)`,
    );

    const assignedRows = Object.values(compositeToRows).reduce(
      (s, arr) => s + arr.length,
      0,
    );
    console.log(
      `📌 Assigned ${assignedRows} rows to composite groups; skipped ${skippedRows} rows`,
    );

    // start validation progress overlay
    setIsValidating(true);
    setValidationProgress(0);

    // Prepare to collect all results
    const allIssues: z.ZodIssue[] = [];
    const allRows: ImportRow[] = [];
    const allMappedRows = new Map<string, (ImportRow | Partial<ImportRow>)[]>();
    const counters = { assets: 0, partials: 0 };
    let excludedRowsByValidation = 0;

    // First, collect ONE representative asset ID per unique composite group to fetch ALL survey data upfront
    const representativeAssetIds: string[] = [];
    for (const [compositeKey, rows] of Object.entries(compositeToRows)) {
      const firstRow = rows[0];
      const representativeAssetId =
        firstRow.Relationship === CHILD_ROW
          ? firstRow['Parent ID']
          : firstRow['ID'];
      if (representativeAssetId) {
        representativeAssetIds.push(representativeAssetId);
      }
    }

    console.log(
      `🔍 Fetching survey data for ${representativeAssetIds.length} representative asset(s) (one per composite group)...`,
    );

    // Fetch ALL survey data upfront in a single batched operation
    const {
      assetIdToSurveyId: initialAssetIdToSurveyId,
      surveyIdToAssetList,
      surveyIdToFormFields,
    } = await getInitialDataForAssetIds(representativeAssetIds, clientId);

    const uniqueSurveys = new Set(Object.values(initialAssetIdToSurveyId));
    console.log(
      `✅ Survey data fetched for ${uniqueSurveys.size} unique survey(s)`,
    );

    // Now build the full assetIdToSurveyId map by associating all assets in each group with their survey
    const assetIdToSurveyId: Record<string, string> = {
      ...initialAssetIdToSurveyId,
    };

    let processedGroups = 0;
    const totalGroups = Object.keys(compositeToRows).length;

    for (const [compositeKey, rows] of Object.entries(compositeToRows)) {
      processedGroups++;
      console.log(
        `🔄 Processing group ${processedGroups}/${totalGroups}: ${compositeKey} (${rows.length} rows)...`,
      );
      // update validation progress
      setValidationProgress(Math.round((processedGroups / totalGroups) * 100));

      // Get the representative asset for this group to look up its surveyId
      const firstRow = rows[0];
      const representativeAssetId =
        firstRow.Relationship === CHILD_ROW
          ? firstRow['Parent ID']
          : firstRow['ID'];

      const surveyId = representativeAssetId
        ? assetIdToSurveyId[representativeAssetId]
        : undefined;

      // Get all unique asset IDs for this group
      const assetIds = Array.from(
        new Set(
          rows.map((row) =>
            row.Relationship === CHILD_ROW ? row['Parent ID'] : row['ID'],
          ),
        ),
      ).filter((id): id is string => Boolean(id));

      // Associate all assets in this group with the same surveyId
      if (surveyId) {
        assetIds.forEach((id) => {
          assetIdToSurveyId[id] = surveyId;
        });
      }

      // Group rows by surveyId (should be only one per compositeKey, but keep logic generic)
      const surveyIdToRows: Record<string, ImportRow[]> = {};
      rows.forEach((row) => {
        const assetId =
          row.Relationship === CHILD_ROW ? row['Parent ID'] : row['ID'];
        const surveyId = assetIdToSurveyId[assetId];
        if (!surveyIdToRows[surveyId]) surveyIdToRows[surveyId] = [];
        surveyIdToRows[surveyId].push(row);
      });

      for (const [surveyId, rowsForSurvey] of Object.entries(surveyIdToRows)) {
        const assetList = surveyIdToAssetList[surveyId];
        const formFields = surveyIdToFormFields[surveyId];
        // If we don't even have a surveyId mapped for this group, log that first
        if (!surveyId) {
          console.error('No surveyId mapped for composite group', {
            compositeKey,
            representativeAssetId,
            rowsForSurvey: rowsForSurvey.length,
            assetIds: rowsForSurvey
              .map((r) =>
                r.Relationship === CHILD_ROW ? r['Parent ID'] : r['ID'],
              )
              .filter(Boolean),
          });
        }

        if (!assetList || !formFields) {
          // Collect asset IDs present in these rows to aid debugging
          const missingAssetIds = Array.from(
            new Set(
              rowsForSurvey
                .map((r) =>
                  r.Relationship === CHILD_ROW ? r['Parent ID'] : r['ID'],
                )
                .filter((id) => Boolean(id)),
            ),
          );

          // Log a clear, structured error so you can inspect which surveyId is missing metadata
          console.error('Missing taxonomy/formFields for survey', {
            surveyId,
            compositeKey,
            rowsForSurvey: rowsForSurvey.length,
            assetIds: missingAssetIds,
            reason:
              !assetList && !formFields
                ? 'missing assetList and formFields'
                : !assetList
                ? 'missing assetList'
                : 'missing formFields',
          });

          allIssues.push({
            code: 'custom',
            path: [0, 'ID'],
            message: `No taxonomy or form fields found for surveyId ${surveyId}. Asset IDs: ${missingAssetIds.join(
              ', ',
            )}`,
          });
          // all rows for this surveyId are effectively excluded
          excludedRowsByValidation += rowsForSurvey.length;
          continue;
        }

        console.log(
          `  ✓ Validating ${rowsForSurvey.length} rows for surveyId: ${surveyId}`,
        );

        const assets: ImportRow[] = [];
        const partials: Partial<ImportRow>[] = [];
        rowsForSurvey.forEach((row) => {
          if (row.Relationship === CHILD_ROW) {
            partials.push(row);
          } else {
            assets.push(row);
          }
        });
        // Validate
        const partialsSchemaCheck = z
          .array(partialImportRowSchema)
          .safeParse(partials, { errorMap });
        if (!partialsSchemaCheck.success) {
          console.warn('Partial rows failed schema validation', {
            surveyId,
            compositeKey,
            partialCount: partials.length,
            issuesCount: partialsSchemaCheck.error.issues.length,
            sampleIssues: partialsSchemaCheck.error.issues.slice(0, 5),
            assetIds: partials.map((p) => p['Parent ID'] || p['ID']),
            rows: partials.map((p) => (p as any).__rowIndex),
          });
          // remap partial indices to original file row indices
          partialsSchemaCheck.error.issues.forEach((issue) => {
            const [localIndex, ...rest] = issue.path as any[];
            const originalRow =
              typeof localIndex === 'number' && partials[localIndex]
                ? (partials[localIndex] as any).__rowIndex
                : undefined;
            if (typeof originalRow === 'number') {
              issue.path = [originalRow, ...rest];
            }
            allIssues.push(issue);
          });
          // exclude these rows from import
          excludedRowsByValidation += partials.length;
        }
        const convertFieldsToZod = formFields.reduce((acc, ff) => {
          let baseType;
          const options = ff.options?.map((o) => o.value?.trim());
          switch (ff.type) {
            case 'Decimal':
            case 'Number':
            case 'Slider':
              baseType = z.coerce
                .number()
                .nonnegative()
                .or(z.literal(NOT_APPLICABLE))
                .nullish();
              break;
            case 'Barcode':
            case 'Text':
              baseType = z.coerce
                .string({ errorMap })
                .or(z.literal(NOT_APPLICABLE))
                .nullish();
              break;
            case 'Date':
              baseType = dateSchema
                .or(z.literal(NOT_APPLICABLE))
                .or(z.undefined())
                .or(z.literal(''))
                .nullish();
              break;
            case 'Select':
            case 'Radio':
              if (!options || options.length === 0) {
                baseType = z.string().or(z.literal(NOT_APPLICABLE)).nullish();
              } else {
                baseType = z
                  .preprocess(
                    (val) => {
                      const stringVal = String(val).trim();
                      if (stringVal === '' || stringVal === 'undefined') {
                        return undefined;
                      }
                      return stringVal;
                    },
                    z
                      .enum(options as [string, ...string[]])
                      .or(z.literal(NOT_APPLICABLE))
                      .nullish(),
                  )
                  .nullish();
              }
              break;
          }
          return { ...acc, [ff.label]: baseType };
        }, {});
        const ffSchema = z.object({ ...convertFieldsToZod });
        const fullAssetSchema = z.array(assetImportRowSchema.merge(ffSchema));
        const assetsSchemaCheck = fullAssetSchema.safeParse(assets, {
          errorMap,
        });
        if (!assetsSchemaCheck.success) {
          console.warn('Asset rows failed schema validation', {
            surveyId,
            compositeKey,
            assetCount: assets.length,
            issuesCount: assetsSchemaCheck.error.issues.length,
            sampleIssues: assetsSchemaCheck.error.issues.slice(0, 5),
            assetIds: assets.map((a) => a['ID']),
            rows: assets.map((a) => (a as any).__rowIndex),
          });
          // remap asset indices to original file row indices
          assetsSchemaCheck.error.issues.forEach((issue) => {
            const [localIndex, ...rest] = issue.path as any[];
            const originalRow =
              typeof localIndex === 'number' && assets[localIndex]
                ? (assets[localIndex] as any).__rowIndex
                : undefined;
            if (typeof originalRow === 'number') {
              issue.path = [originalRow, ...rest];
            }
            allIssues.push(issue);
          });
          // exclude these rows from import
          excludedRowsByValidation += assets.length;
        }
        // Map rows for import
        if (partialsSchemaCheck.success && assetsSchemaCheck.success) {
          [...partialsSchemaCheck.data, ...assetsSchemaCheck.data].forEach(
            (row) => {
              const isPartial = row['Relationship'] === CHILD_ROW;
              const assetId = isPartial ? row['Parent ID'] : row['ID'];
              const existingOrNew = allMappedRows.get(assetId) || [];
              allMappedRows.set(assetId, [...existingOrNew, row]);
              // Also map assetId to surveyId for downstream use
              assetIdToSurveyId[assetId] = surveyId;
            },
          );
          allRows.push(...rows);
          // assets array already contains rows where Relationship !== CHILD_ROW
          counters.assets += assets.length;
          counters.partials += partials.length;
        }
      }
    } // <-- Close the main for...of loop and function block

    console.log(
      `✅ Validation complete. Total issues found: ${allIssues.length}`,
    );
    setValidationProgress(100);
    setIsValidating(false);
    // close the overlay modal
    // @ts-ignore
    closeModal?.();
    const mappedRows = allRows.length;
    console.log(
      `� Summary: assignedRows=${assignedRows}, mappedRows=${mappedRows}, excludedByValidation=${excludedRowsByValidation}, skippedRows=${skippedRows}`,
    );
    console.log(
      `�📈 Ready to import: ${counters.assets} assets, ${counters.partials} partial renewals`,
    );

    if (allIssues.length) {
      const parsed = allIssues.map((issue) => {
        console.log({ issue });
        const [row, column] = (issue.path || [0, 'ID']) as [number, string];
        if (issue.code === z.ZodIssueCode.invalid_union) {
          const firstIssue = (issue as any).unionErrors[0].issues[0];
          return {
            row: typeof row === 'number' ? row + 1 : row,
            column: firstIssue.path ? firstIssue.path.join('.') : column,
            message: firstIssue.message,
          };
        }
        return {
          row: typeof row === 'number' ? row + 1 : row,
          column,
          message: (issue as any).message,
        };
      });
      setInitialErrors(parsed);
      // errors will be shown in the page (initialErrors). Close overlay and return.
      return;
    } else {
      setInitialErrors([]);
    }
    setImportData({
      allRows,
      mappedRows: allMappedRows,
      name,
      counters,
      errors: [],
      assetIdToSurveyId,
      surveyIdToFormFields,
      surveyIdToAssetList,
    });
  };

  const onClickedRemove = () => {
    setImportData(undefined);
  };

  const onClickStartImport = async () => {
    if (clientId && importData?.mappedRows) {
      setIsImporting(true);
      const assetIds = Array.from(importData.mappedRows.keys());
      const total = assetIds.length;
      setUpdateTotal(total);
      setUpdateProgress(0);
      setUpdateResults([]);

      // Process assets in batches to avoid "Too many outstanding requests"
      const BATCH_SIZE = 100; // Adjust this based on your needs
      const batches: string[][] = [];

      for (let i = 0; i < assetIds.length; i += BATCH_SIZE) {
        batches.push(assetIds.slice(i, i + BATCH_SIZE));
      }

      let completed = 0;

      // Process batches sequentially
      for (const batch of batches) {
        // Process assets in current batch concurrently
        const batchPromises = batch.map(async (assetId) => {
          const rowMap = importData.mappedRows.get(assetId);

          // Determine the correct surveyId for this asset
          let surveyId: string | undefined;
          const nonPartialRow = rowMap?.find(
            (row) => row.Relationship !== CHILD_ROW,
          );

          if (nonPartialRow) {
            surveyId = importData.assetIdToSurveyId?.[assetId];
          } else {
            // For partials, try to get parent asset's surveyId
            const parentId = (rowMap?.[0] as Partial<ImportRow>)['Parent ID'];
            if (parentId && importData.assetIdToSurveyId?.[parentId]) {
              surveyId = importData.assetIdToSurveyId[parentId];
            }
          }

          const results = await updateAssetFromImportRow(
            assetId,
            clientId,
            importData,
            surveyId ? importData.surveyIdToFormFields[surveyId] : [],
            surveyId ? importData.surveyIdToAssetList[surveyId] : [],
          );

          return results;
        });

        // Wait for current batch to complete
        const batchResults = await Promise.all(batchPromises);

        // Update progress and results
        batchResults.forEach((results) => {
          setUpdateResults((prev: UpdateResult[]) => [...prev, ...results]);
          completed++;
          setUpdateProgress(completed);
        });
      }

      setIsImporting(false);
    }
  };

  // Helper to count updated assets and partials by survey name
  function getUpdatedBySurveyName(
    updateResults: UpdateResult[],
    importData: ImportData,
  ): Record<string, { assets: number; partials: number }> {
    if (!importData) return {};
    const surveyIdToSurveyName: Record<string, string> = {};
    // Build surveyId to surveyName map from allRows
    importData.allRows.forEach((row) => {
      const assetId = row['ID'] || row['Parent ID'];
      if (!assetId) return;
      const surveyId = importData.assetIdToSurveyId?.[assetId];
      if (surveyId && row['Survey Name']) {
        surveyIdToSurveyName[surveyId] = row['Survey Name'];
      }
    });
    // Count assets and partials updated by surveyId
    const surveyIdToCount: Record<
      string,
      { assets: number; partials: number }
    > = {};
    // Only count actual successful updates (not warnings)
    updateResults.forEach((result) => {
      if (result.success?.type === 'asset' && result.success.assetId) {
        const surveyId = importData.assetIdToSurveyId?.[result.success.assetId];
        if (surveyId) {
          if (!surveyIdToCount[surveyId])
            surveyIdToCount[surveyId] = { assets: 0, partials: 0 };
          surveyIdToCount[surveyId].assets++;
        }
      }
      if (
        result.success?.type === 'partial' &&
        result.success.assetId &&
        // Only count if there was an actual change (not a warning)
        !result.warning &&
        !result.error
      ) {
        const surveyId = importData.assetIdToSurveyId?.[result.success.assetId];
        if (surveyId) {
          if (!surveyIdToCount[surveyId])
            surveyIdToCount[surveyId] = { assets: 0, partials: 0 };
          surveyIdToCount[surveyId].partials++;
        }
      }
    });
    // Map surveyId to surveyName and counts
    const surveyNameToCount: Record<
      string,
      { assets: number; partials: number }
    > = {};
    Object.entries(surveyIdToCount).forEach(([surveyId, counts]) => {
      const surveyName = surveyIdToSurveyName[surveyId] || surveyId;
      surveyNameToCount[surveyName] = counts;
    });
    return surveyNameToCount;
  }

  const updatedBySurveyName = useMemo(() => {
    return getUpdatedBySurveyName(updateResults, importData!);
  }, [updateResults, importData]);

  const warnings = useMemo(() => {
    return updateResults
      .filter((u) => !!u.warning)
      .map((u) => u.warning)
      .sort(sortByRow);
  }, [updateResults]);

  const errors = useMemo(() => {
    return updateResults
      .filter((u) => !!u.error)
      .map((u) => u.error)
      .sort(sortByRow);
  }, [updateResults]);

  const assetSuccesses: UpdateResult[] = useMemo(() => {
    return updateResults
      .filter((u) => u.success?.type === 'asset')
      .sort(sortByRow);
  }, [updateResults]);

  const partialSuccesses: UpdateResult[] = useMemo(() => {
    return updateResults
      .filter((u) => u.success?.type === 'partial')
      .sort(sortByRow);
  }, [updateResults]);

  return (
    <Grid container spacing={2} alignItems="stretch">
      <Grid item xs={12}>
        <Paper sx={{ mb: 2 }} id="main-content">
          <Box
            p={2}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
          >
            <Chip
              sx={{ mb: 2 }}
              variant="outlined"
              label="Step 1"
              color="primary"
              role="presentation"
            />

            <Typography color="secondary" variant="body1">
              Export asset data from{' '}
              <Link
                to={`/${clientId}/reporting/create`}
                component={NavLink}
                underline="hover"
              >
                here
              </Link>
              .
            </Typography>

            <Chip
              sx={{ my: 2 }}
              variant="outlined"
              label="Step 2"
              color="primary"
              role="presentation"
            />

            <Typography color="secondary" variant="body1" align="left">
              Ensure any data you amend is valid for the survey.
            </Typography>
            <Typography color="secondary" variant="body1">
              The following columns may be updated:
            </Typography>
            <Grid container>
              <Grid item xs={12} sm={6} lg={3}>
                <Stack>
                  <Typography mt={2} color="primary" align="left">
                    Asset:
                  </Typography>
                  <Typography color="secondary" variant="caption" align="left">
                    Code 0<br />
                    Label 0<br />
                    Code 1<br />
                    Label 1<br />
                    Code 2<br />
                    Label 2<br />
                    Code 3<br />
                    Label 3<br />
                    Description
                    <br />
                    Quantity
                    <br />
                    UOM
                    <br />
                    Cost
                    <br />
                    Uplift Multiplier
                    <br />
                    + Any bespoke fields added for this survey
                    <br />
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <Stack>
                  <Typography mt={2} color="primary" align="left">
                    Partial Renewal:
                  </Typography>
                  <Typography color="secondary" variant="caption" align="left">
                    Description
                    <br />
                    Renewal Multiplier
                    <br />
                    Remaining Life
                    <br />
                    Expected Life
                    <br />
                    Reccurs
                    <br />
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
            <Chip
              sx={{ my: 2 }}
              variant="outlined"
              label="Step 3"
              color="primary"
              role="presentation"
            />

            <Typography color="secondary" variant="body1">
              Upload your edited asset data below and follow the prompts.
            </Typography>
          </Box>
        </Paper>
        <Paper>
          <FileUploaderCard
            ref={fileUploaderRef}
            // @ts-ignore
            title="Asset Data Import"
            onChange={(file) => setFileData(file)}
            onRemove={importData && onClickedRemove}
            fileName={importData?.name}
          />
        </Paper>
        {/* Validation progress is shown via a modal overlay using showModal */}
        {initialErrors?.length ? (
          <Paper sx={{ mt: 2, p: 2 }}>
            <Typography color="error" mt={2} paragraph>
              No asset data has been changed.
            </Typography>
            <Typography color="error">
              The following errors occured, please correct them and try again:
            </Typography>
            <Box mt={2}>
              {initialErrors.map((error) => (
                <Box key={`${error.row}${error.column}`} mt={2}>
                  <Typography variant="body2" color="secondary">
                    Row:{Number(error.row) + 1}, Column:{error.column}
                    {' - '}
                    {error.message}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        ) : null}
        {!updateResults.length && importData?.allRows?.length ? (
          <Paper sx={{ mt: 2, p: 2 }}>
            <Typography color="primary" mb={2}>
              Asset data is valid and ready to be imported &#10003;
            </Typography>
            <Typography color="primary" mb={2} paragraph></Typography>

            <Typography color="primary">
              {handlePlural(importData.counters.assets, 'assets', true)}
            </Typography>
            {importData.counters.partials ? (
              <Typography color="primary">
                {' '}
                and{' '}
                {handlePlural(
                  importData.counters.partials,
                  'partial renewals',
                  true,
                )}
              </Typography>
            ) : null}

            <Typography color="primary">
              {' '}
              will be checked for updates
            </Typography>

            <Button
              sx={{ display: 'block', mt: 2 }}
              onClick={onClickStartImport}
              variant="contained"
              disabled={isImporting}
            >
              {isImporting ? 'Importing...' : 'Start Import'}
            </Button>
          </Paper>
        ) : null}
        {/* Show progress while updates are running */}
        {(updateProgress > 0 || isImporting) && updateProgress < updateTotal ? (
          <Paper sx={{ mt: 2, p: 2 }}>
            <Typography color="primary" mb={2}>
              Please wait... Updated {updateProgress} of {updateTotal} assets...
            </Typography>
          </Paper>
        ) : null}
        {/* Only show complete message when all updates are done */}
        {importData?.name &&
        updateResults.length &&
        updateProgress === updateTotal ? (
          <Paper sx={{ mt: 2, p: 2 }}>
            <Typography color="primary" mb={2} paragraph>
              Asset data import complete &#10003;
            </Typography>

            {assetSuccesses.length || partialSuccesses.length ? (
              <>
                <Box mt={2}>
                  <Stack
                    display={'flex'}
                    direction="row"
                    spacing={1}
                    mb={1}
                    width={'100%'}
                    justifyContent={'space-between'}
                  >
                    <Typography
                      color="primary"
                      variant="body2"
                      fontWeight={700}
                      width={'50%'}
                    >
                      Survey Name
                    </Typography>
                    <Typography
                      color="primary"
                      variant="body2"
                      fontWeight={700}
                      width={'50%'}
                    >
                      Updates
                    </Typography>
                  </Stack>
                  {Object.entries(updatedBySurveyName).map(
                    ([surveyName, counts]) => (
                      <Stack
                        key={surveyName}
                        display={'flex'}
                        direction="row"
                        spacing={1}
                        width={'100%'}
                        justifyContent={'space-between'}
                        borderTop={1}
                        borderColor={colours.lightGrey}
                        py={1}
                      >
                        <Typography
                          color="primary"
                          variant="body2"
                          width={'50%'}
                        >
                          {surveyName}:
                        </Typography>
                        <Typography
                          color="primary"
                          variant="body2"
                          width={'50%'}
                        >
                          {counts.assets > 0 && (
                            <>
                              {counts.assets} asset
                              {counts.assets !== 1 ? 's' : ''} updated
                            </>
                          )}
                          {counts.assets > 0 && counts.partials > 0 ? ', ' : ''}
                          {counts.partials > 0 && (
                            <>
                              {counts.partials} partial renewal
                              {counts.partials !== 1 ? 's' : ''} updated
                            </>
                          )}
                        </Typography>
                      </Stack>
                    ),
                  )}
                </Box>
              </>
            ) : null}

            {errors.length ? (
              <Typography mb={1} mt={2} sx={{ color: colours.red }}>
                Errors:
              </Typography>
            ) : null}
            {errors.length
              ? errors.map((e) => (
                  <Typography variant="caption" paragraph mb={1} key={e?.row}>
                    Row {e?.row}: {e?.message}
                  </Typography>
                ))
              : null}

            {warnings.length ? (
              <Typography
                mb={1}
                sx={{ color: colours.orange }}
                paragraph
                mt={2}
              >
                Warnings:
              </Typography>
            ) : null}
            {warnings.length
              ? warnings.map((w) => (
                  <Typography variant="caption" paragraph mb={1} key={w?.row}>
                    Row {w?.row}: {w?.message}
                  </Typography>
                ))
              : null}
          </Paper>
        ) : null}
      </Grid>
    </Grid>
  );
};

export default AssetDataImport;
